import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  type AdminApproval,
  AdminApprovalStatus,
  AdminApprovalType,
  adminapprovalListAdminApprovals,
  adminapprovalListAdminApprovalsQueryKey,
} from '@column/api';

import { getEffectiveStatus } from '~/lib/approvals/utils';

const PAGE_SIZE = 100;

const listAllPending = async (type: AdminApprovalType) => {
  const approvals: AdminApproval[] = [];
  let startingAfter: string | undefined;
  for (;;) {
    const { data } = await adminapprovalListAdminApprovals({
      query: { type, status: AdminApprovalStatus.PENDING, limit: PAGE_SIZE, starting_after: startingAfter },
      throwOnError: true,
    });
    approvals.push(...data.admin_approvals);
    const lastId = data.admin_approvals.at(-1)?.id;
    if (!data.has_more || !lastId || lastId === startingAfter) return approvals;
    startingAfter = lastId;
  }
};

export const usePendingAdminApprovalsQuery = (type: AdminApprovalType, enabled = true) => {
  const query = useQuery({
    queryKey: adminapprovalListAdminApprovalsQueryKey({ query: { type, status: AdminApprovalStatus.PENDING } }),
    queryFn: () => listAllPending(type),
    retry: false,
    enabled,
  });
  const approvals = useMemo(
    () => (query.data ?? []).filter((approval) => getEffectiveStatus(approval) === AdminApprovalStatus.PENDING),
    [query.data]
  );
  return { ...query, approvals };
};

export const usePendingAdminApprovals = (type: AdminApprovalType) => usePendingAdminApprovalsQuery(type).approvals;
